import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import RoomMediaManager from '../components/RoomMediaManager';
import CustomMapManager from '../components/CustomMapManager';
import ThemeSettings from '../components/ThemeSettings';
import CampMapEditor from '../components/CampMapEditor';
import PropertyOwnersManager from '../components/PropertyOwnersManager';
import EmailSettingsManager from '../components/EmailSettingsManager';
import PromoCodesManager from '../components/PromoCodesManager';
import InfoCardsManager from '../components/InfoCardsManager';
import RatesManager from '../components/RatesManager';
import RijekaButton from '../components/RijekaButton';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('settings'); // 'settings', 'media', 'theme'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [settings, setSettings] = useState({
    beds24_api_key: '',
    pms_api_key: '',
    pms_tenant_slug: '',
    pms_api_base_url: '',
    pms_webhook_secret: '',
    stripe_api_key: '',
    stripe_publishable_key: '',
    property_configs: []
  });
  const [pmsTesting, setPmsTesting] = useState(false);
  const [pmsTestResult, setPmsTestResult] = useState(null);
  const [pmsConnecting, setPmsConnecting] = useState(false);
  
  const [newProperty, setNewProperty] = useState({
    property_id: '',
    property_name: '',
    room_ids: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchSettings(token);
    fetchTokenStatus();
  }, [navigate]);

  const fetchSettings = async (token) => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        toast.error('Failed to load settings');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    setSaving(true);

    try {
      await axios.post(`${API}/admin/settings`, {
        beds24_api_key: settings.beds24_api_key,
        pms_api_key: settings.pms_api_key,
        pms_tenant_slug: settings.pms_tenant_slug,
        pms_api_base_url: settings.pms_api_base_url,
        pms_webhook_secret: settings.pms_webhook_secret,
        stripe_api_key: settings.stripe_api_key,
        stripe_publishable_key: settings.stripe_publishable_key
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      toast.success('Settings saved successfully');
      setTestResult(null);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const fetchTokenStatus = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/admin/token-status`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTokenStatus(response.data);
    } catch (error) {
      console.error('Error fetching token status:', error);
    }
  };

  const handleRefreshToken = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    setRefreshing(true);

    try {
      const response = await axios.post(`${API}/admin/refresh-token`, {}, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.data.status === 'success') {
        toast.success('Token refreshed successfully!');
        await fetchTokenStatus(); // Refresh the status display
      } else {
        toast.error(response.data.message || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      toast.error(error.response?.data?.message || 'Failed to refresh token');
    } finally {
      setRefreshing(false);
    }
  };

  const handleTestBeds24 = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const response = await axios.get(`${API}/admin/test-beds24`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setTestResult(response.data);
      if (response.data.success) {
        toast.success('Beds24 connection successful!');
        // Also fetch token status after successful connection
        await fetchTokenStatus();
      } else {
        toast.error('Beds24 connection failed');
      }
    } catch (error) {
      console.error('Error testing Beds24:', error);
      setTestResult({
        success: false,
        message: error.response?.data?.detail || error.message,
        hint: 'Please check your invite code'
      });
      toast.error('Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleTestPms = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    setPmsTesting(true);
    setPmsTestResult(null);
    try {
      // Save current PMS settings first so backend uses fresh slug/base_url
      await axios.post(`${API}/admin/settings`, {
        pms_api_key: settings.pms_api_key,
        pms_tenant_slug: settings.pms_tenant_slug,
        pms_api_base_url: settings.pms_api_base_url,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const response = await axios.get(`${API}/admin/test-pms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPmsTestResult(response.data);
      if (response.data.success) {
        toast.success('PMS connection successful!');
      } else {
        toast.error('PMS connection failed');
      }
    } catch (error) {
      console.error('Error testing PMS:', error);
      setPmsTestResult({
        success: false,
        message: error.response?.data?.detail || error.message,
        hint: 'Please check your PMS API Key, Tenant Slug and Base URL',
      });
      toast.error('Failed to test PMS connection');
    } finally {
      setPmsTesting(false);
    }
  };

  const handleConnectWebhook = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    setPmsConnecting(true);
    try {
      // Ensure the latest slug/base_url/api_key are persisted first so the
      // backend uses fresh values when it calls PMS /connect.
      await axios.post(`${API}/admin/settings`, {
        pms_api_key: settings.pms_api_key,
        pms_tenant_slug: settings.pms_tenant_slug,
        pms_api_base_url: settings.pms_api_base_url,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const webhookUrl = `${window.location.origin}/api/pms/webhook`;
      const response = await axios.post(
        `${API}/admin/pms/connect-webhook`,
        { webhook_url: webhookUrl, description: `Booking Engine on ${window.location.host}` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        response.data.rotated
          ? 'Webhook rotiran — novi secret spremljen.'
          : 'Webhook uspješno povezan! PMS će poslati test event za par sekundi.'
      );
      // Refresh settings so status widget shows the new webhook_id + timestamp
      const fresh = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(fresh.data);
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      toast.error(`Webhook connect failed: ${detail}`);
    } finally {
      setPmsConnecting(false);
    }
  };

  const handleAddProperty = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    if (!newProperty.property_id || !newProperty.property_name || !newProperty.room_ids) {
      toast.error('Please fill in all property fields');
      return;
    }

    try {
      const roomIdsArray = newProperty.room_ids.split(',').map(id => id.trim()).filter(id => id);
      
      await axios.post(`${API}/admin/properties`, {
        property_id: newProperty.property_id,
        property_name: newProperty.property_name,
        room_ids: roomIdsArray
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success('Property added successfully');
      setNewProperty({ property_id: '', property_name: '', room_ids: '' });
      fetchSettings(token);
    } catch (error) {
      console.error('Error adding property:', error);
      toast.error('Failed to add property');
    }
  };

  const handleEditProperty = (property) => {
    setEditingProperty({
      ...property,
      room_ids: property.room_ids.join(', '),
      group_name: property.group_name || '',
      display_order: property.display_order || 0,
      pricing_policy_hr: property.pricing_policy_hr || '',
      pricing_policy_en: property.pricing_policy_en || '',
      pricing_policy_de: property.pricing_policy_de || '',
      pricing_policy_it: property.pricing_policy_it || ''
    });
  };

  const handleSaveEdit = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    if (!editingProperty.property_id || !editingProperty.property_name || !editingProperty.room_ids) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const roomIdsArray = editingProperty.room_ids.split(',').map(id => id.trim()).filter(id => id);
      
      await axios.post(`${API}/admin/properties`, {
        property_id: editingProperty.property_id,
        property_name: editingProperty.property_name,
        room_ids: roomIdsArray,
        group_name: editingProperty.group_name || null,
        display_order: parseInt(editingProperty.display_order) || 0,
        pricing_policy_hr: editingProperty.pricing_policy_hr || null,
        pricing_policy_en: editingProperty.pricing_policy_en || null,
        pricing_policy_de: editingProperty.pricing_policy_de || null,
        pricing_policy_it: editingProperty.pricing_policy_it || null
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      toast.success('Property updated successfully');
      setEditingProperty(null);
      fetchSettings(token);
    } catch (error) {
      console.error('Error updating property:', error);
      toast.error('Failed to update property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('Not authenticated');
      navigate('/admin');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this property (ID: ${propertyId})?`)) {
      return;
    }

    try {
      console.log('Deleting property:', propertyId);
      const response = await axios.delete(`${API}/admin/properties/${propertyId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      console.log('Delete response:', response.data);
      toast.success('Property deleted successfully');
      
      // Refresh the settings
      await fetchSettings(token);
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to delete property');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="inline-block animate-spin h-16 w-16 border-4 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {/* Navigation - Logo Only */}
      <nav className="bg-[var(--color-dark)] text-white shadow-lg" style={{padding: '10px 0px' , borderBottom: '1px solid var(--color-primary-dark)'}}>
        <div className="container mx-auto px-6 py-5 flex justify-between items-center">
          <div 
            className="cursor-pointer hover:opacity-80 transition-opacity flex items-center" 
            onClick={() => navigate('/')}
          >
            {theme.logo_url ? (
              <img src={theme.logo_url} alt="Logo" className="h-10 w-auto" />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            )}
          </div>
          <RijekaButton 
            variant="outline" 
            onClick={handleLogout}
            data-testid="logout-button"
            className="border-white text-white hover:bg-white hover:text-[var(--color-text-primary)]"
          >
            Logout
          </RijekaButton>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-12" style={{padding: '50px 20px'}}>
        <div className="max-w-7xl mx-auto">
          <div className="bg-white p-8 mb-8 shadow-lg border-l-4 border-[var(--color-primary)]">
            <h2 className="text-4xl md:text-5xl mb-3 font-bold text-[var(--color-text-primary)]">Admin Dashboard</h2>
            <p className="text-gray-600 text-lg">Manage your properties, rooms, and system settings</p>
          </div>

          {/* Tabs */}
          {(() => {
            const tabs = [
              { id: 'settings', label: 'SETTINGS & API KEYS' },
              { id: 'media', label: 'ROOM MEDIA' },
              { id: 'campmap', label: 'KAMP MAPA' },
              { id: 'custommap', label: 'CUSTOM MAPA' },
              { id: 'theme', label: 'THEME & BRANDING' },
              { id: 'email', label: 'EMAIL' },
              { id: 'promo', label: 'PROMO KODOVI' },
              { id: 'cards', label: 'INFO KARTICE' },
              { id: 'rates', label: 'RATE-OVI' },
            ];
            return (
              <div className="flex gap-2 mb-8 flex-wrap">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-8 py-4 font-bold text-base transition-all ${
                      activeTab === tab.id
                        ? 'bg-[var(--color-primary)] text-white'
                        : 'bg-white text-[var(--color-text-primary)] hover:bg-[var(--color-border)]'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Settings Tab Content */}
          {activeTab === 'settings' && (
            <>
              {/* Admin Credentials Info */}
              <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h3 className="font-medium mb-2">Admin Credentials</h3>
              <div className="space-y-1 text-sm">
                <p><strong>Username:</strong> admin (postavljen u backend/.env)</p>
                <p><strong>Password:</strong> postavljen kao ADMIN_PASSWORD u backend/.env</p>
                <p className="text-xs text-[var(--color-text-secondary)] mt-2">Promijeni preko ADMIN_USERNAME / ADMIN_PASSWORD env varijabli i restartiraj backend.</p>
              </div>
            </CardContent>
          </Card>

          {/* API Configuration */}
          <Card className="mb-6" data-testid="admin-settings-form">
            <CardHeader>
              <CardTitle>API Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* PMS (RentalWeb) Settings */}
              <div className="border-2 border-emerald-200 bg-emerald-50/40 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-600 text-white text-xs font-semibold">NEW</span>
                  <h3 className="text-base font-semibold text-emerald-900">RentalWeb PMS (Channel Manager)</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pmsKey">PMS API Key</Label>
                    <Input
                      id="pmsKey"
                      type="password"
                      value={settings.pms_api_key || ''}
                      onChange={(e) => setSettings({ ...settings, pms_api_key: e.target.value })}
                      placeholder="rw_..."
                      className="font-mono text-sm"
                      data-testid="pms-key-input"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                      Generirajte u PMS adminu: Settings → API → Rotate API Key.
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="pmsSlug">Tenant Slug</Label>
                    <Input
                      id="pmsSlug"
                      type="text"
                      value={settings.pms_tenant_slug || ''}
                      onChange={(e) => setSettings({ ...settings, pms_tenant_slug: e.target.value })}
                      placeholder="npr. rijekabooking"
                      className="font-mono text-sm"
                      data-testid="pms-slug-input"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">
                      Vaš tenant slug u PMS-u (dio URL-a).
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <Label htmlFor="pmsBaseUrl">PMS Base URL</Label>
                  <Input
                    id="pmsBaseUrl"
                    type="text"
                    value={settings.pms_api_base_url || ''}
                    onChange={(e) => setSettings({ ...settings, pms_api_base_url: e.target.value })}
                    placeholder="https://booking-hub-692.preview.emergentagent.com"
                    className="font-mono text-sm"
                    data-testid="pms-base-url-input"
                  />
                  <p className="text-xs text-[var(--color-text-muted)] mt-1">
                    URL vašeg PMS-a (bez zaglavlja /api).
                  </p>
                </div>

                <div className="p-4 border rounded-lg bg-slate-50">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <Label className="text-sm font-semibold">PMS Real-Time Webhook</Label>
                      <p className="text-xs text-[var(--color-text-muted)] mt-1">
                        Kad se registrira, PMS odmah šalje događaje (nova rezervacija, promjena cijene, restrikcija) → kalendar postaje real-time.
                      </p>
                    </div>
                    {settings.pms_webhook_id ? (
                      <span
                        data-testid="pms-webhook-status-connected"
                        className="text-xs font-semibold px-2 py-1 bg-emerald-100 text-emerald-800 rounded whitespace-nowrap"
                      >
                        Connected
                      </span>
                    ) : (
                      <span
                        data-testid="pms-webhook-status-disconnected"
                        className="text-xs font-semibold px-2 py-1 bg-gray-200 text-gray-700 rounded whitespace-nowrap"
                      >
                        Not connected
                      </span>
                    )}
                  </div>

                  {settings.pms_webhook_id && (
                    <div className="text-xs font-mono bg-white border rounded p-2 mb-3 space-y-1">
                      <div><span className="text-gray-500">Webhook ID:</span> {settings.pms_webhook_id}</div>
                      {settings.pms_webhook_connected_at && (
                        <div><span className="text-gray-500">Connected:</span> {new Date(settings.pms_webhook_connected_at).toLocaleString('hr-HR')}</div>
                      )}
                      {settings.pms_webhook_url && (
                        <div className="break-all"><span className="text-gray-500">URL:</span> {settings.pms_webhook_url}</div>
                      )}
                    </div>
                  )}

                  <Button
                    type="button"
                    onClick={handleConnectWebhook}
                    disabled={pmsConnecting || !settings.pms_api_key || !settings.pms_tenant_slug || !settings.pms_api_base_url}
                    variant="outline"
                    data-testid="pms-connect-webhook-btn"
                  >
                    {pmsConnecting
                      ? 'Connecting…'
                      : settings.pms_webhook_id
                        ? 'Reconnect / Rotate Secret'
                        : 'Connect Webhook'}
                  </Button>
                  <p className="text-xs text-[var(--color-text-muted)] mt-2">
                    Prvo unesi <strong>PMS API Key</strong>, <strong>Tenant Slug</strong> i <strong>Base URL</strong> gore i klikni <em>Save Settings</em>. Zatim klikni <em>Connect Webhook</em> — mi automatski registriramo endpoint kod PMS-a, primimo generirani secret i spremimo ga interno.
                  </p>
                </div>

                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={handleTestPms}
                    disabled={pmsTesting || !settings.pms_tenant_slug || !settings.pms_api_base_url}
                    variant="outline"
                    size="sm"
                    data-testid="test-pms-button"
                    className="bg-white"
                  >
                    {pmsTesting ? 'Testing PMS...' : 'Test PMS Connection'}
                  </Button>
                </div>

                {pmsTestResult && (
                  <div className={`mt-3 p-3 rounded text-sm ${
                    pmsTestResult.success
                      ? 'bg-green-50 border border-green-200 text-green-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    <p className="font-medium">{pmsTestResult.message}</p>
                    {pmsTestResult.success && pmsTestResult.property_count !== undefined && (
                      <div className="mt-2">
                        <p className="text-xs font-medium">Found {pmsTestResult.property_count} properties:</p>
                        {pmsTestResult.properties && pmsTestResult.properties.length > 0 && (
                          <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                            {pmsTestResult.properties.map((prop) => (
                              <div key={prop.id} className="bg-white p-2 rounded border border-green-300">
                                <p className="font-medium">{prop.name}</p>
                                <p className="text-xs">Property ID: <span className="font-mono">{prop.id}</span></p>
                                {prop.rooms && prop.rooms.length > 0 && (
                                  <div className="mt-1">
                                    <p className="text-xs font-medium">Units:</p>
                                    <ul className="text-xs ml-2">
                                      {prop.rooms.map((room) => (
                                        <li key={room.id}>{room.name} (ID: <span className="font-mono">{room.id}</span>)</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {!pmsTestResult.success && pmsTestResult.hint && (
                      <p className="text-xs mt-1">{pmsTestResult.hint}</p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="beds24Key">Beds24 Invite Code</Label>
                <Input
                  id="beds24Key"
                  type="text"
                  value={settings.beds24_api_key}
                  onChange={(e) => setSettings({ ...settings, beds24_api_key: e.target.value })}
                  placeholder="Enter your Beds24 invite code"
                  className="font-mono text-sm"
                  data-testid="beds24-key-input"
                />
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  Generate from Beds24 Settings → API → Generate Invite Code
                </p>
                
                <div className="flex gap-2 mt-3">
                  <Button 
                    onClick={handleTestBeds24}
                    disabled={testing || !settings.beds24_api_key}
                    variant="outline"
                    size="sm"
                    data-testid="test-beds24-button"
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </Button>
                </div>
                
                {testResult && (
                  <div className={`mt-3 p-3 rounded text-sm ${
                    testResult.success 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    <p className="font-medium">{testResult.message}</p>
                    {testResult.success && testResult.property_count !== undefined && (
                      <div className="mt-2">
                        <p className="text-xs font-medium">Found {testResult.property_count} properties:</p>
                        {testResult.properties && testResult.properties.length > 0 && (
                          <div className="mt-2 space-y-2 max-h-60 overflow-y-auto">
                            {testResult.properties.map((prop) => (
                              <div key={prop.id} className="bg-white p-2 rounded border border-green-300">
                                <p className="font-medium">{prop.name}</p>
                                <p className="text-xs">Property ID: <span className="font-mono">{prop.id}</span></p>
                                {prop.rooms && prop.rooms.length > 0 && (
                                  <div className="mt-1">
                                    <p className="text-xs font-medium">Rooms:</p>
                                    <ul className="text-xs ml-2">
                                      {prop.rooms.map((room) => (
                                        <li key={room.id}>
                                          {room.name} (ID: <span className="font-mono">{room.id}</span>)
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-xs mt-2 italic">Copy these IDs to configure your properties below</p>
                        <Button 
                          onClick={() => {
                            if (testResult.properties && testResult.properties.length > 0) {
                              const prop = testResult.properties[0];
                              const roomIds = prop.rooms.map(r => r.id).join(', ');
                              setNewProperty({
                                property_id: prop.id,
                                property_name: prop.name,
                                room_ids: roomIds
                              });
                              toast.success('Auto-filled with first property');
                            }
                          }}
                          size="sm"
                          variant="outline"
                          className="mt-2"
                        >
                          Auto-fill First Property
                        </Button>
                      </div>
                    )}
                    {!testResult.success && testResult.hint && (
                      <p className="text-xs mt-1">{testResult.hint}</p>
                    )}
                  </div>
                )}

                {/* Token Status Display */}
                {tokenStatus && (
                  <div className={`mt-4 p-4 rounded-lg border ${
                    tokenStatus.status === 'valid' 
                      ? 'bg-green-50 border-green-200' 
                      : tokenStatus.status === 'expiring_soon'
                      ? 'bg-yellow-50 border-yellow-300'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-sm mb-1">
                          {tokenStatus.status === 'valid' ? '✅ Token Active' :
                           tokenStatus.status === 'expiring_soon' ? '⚠️ Token Expiring Soon' :
                           tokenStatus.status === 'expired' ? '❌ Token Expired' :
                           '⚠️ Token Status'}
                        </h4>
                        <p className="text-xs">{tokenStatus.message}</p>
                        {tokenStatus.hours_until_expiry !== undefined && (
                          <p className="text-xs mt-1">
                            {tokenStatus.hours_until_expiry < 0 
                              ? 'Expired' 
                              : `Expires in ${Math.floor(tokenStatus.hours_until_expiry)}h ${Math.floor((tokenStatus.hours_until_expiry % 1) * 60)}m`}
                          </p>
                        )}
                      </div>
                      <Button
                        onClick={handleRefreshToken}
                        disabled={refreshing}
                        size="sm"
                        variant="outline"
                        className="ml-4"
                      >
                        {refreshing ? 'Refreshing...' : 'Refresh Now'}
                      </Button>
                    </div>
                    {tokenStatus.status === 'expired' && (
                      <p className="text-xs mt-3 p-2 bg-white rounded">
                        💡 <strong>To fix:</strong> Generate a new invite code in Beds24 Settings → API, 
                        paste it above, click "Test Connection", then try "Refresh Now"
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-[var(--color-border)] pt-6">
                <h3 className="text-lg font-medium mb-4">Stripe Configuration</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="stripeSecret">Stripe Secret Key</Label>
                    <Input
                      id="stripeSecret"
                      type="password"
                      value={settings.stripe_api_key}
                      onChange={(e) => setSettings({ ...settings, stripe_api_key: e.target.value })}
                      placeholder="sk_test_..."
                      className="font-mono text-sm"
                      data-testid="stripe-secret-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="stripePublishable">Stripe Publishable Key</Label>
                    <Input
                      id="stripePublishable"
                      type="text"
                      value={settings.stripe_publishable_key}
                      onChange={(e) => setSettings({ ...settings, stripe_publishable_key: e.target.value })}
                      placeholder="pk_test_..."
                      className="font-mono text-sm"
                      data-testid="stripe-publishable-input"
                    />
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button 
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="w-full md:w-auto"
                  data-testid="save-settings-button"
                >
                  {saving ? 'Saving...' : 'Save API Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Property Management */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Property & Room Configuration</CardTitle>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">Add your Beds24 property IDs and room IDs</p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Existing Properties */}
              {settings.property_configs && settings.property_configs.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium">Configured Properties</h4>
                  {settings.property_configs.map((prop) => (
                    <div key={prop.property_id} className="border border-[var(--color-border)] rounded p-4">
                      {editingProperty?.property_id === prop.property_id ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div>
                            <Label>Property Name</Label>
                            <Input
                              value={editingProperty.property_name}
                              onChange={(e) => setEditingProperty({...editingProperty, property_name: e.target.value})}
                            />
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">Ime za identifikaciju u admin panelu</p>
                          </div>
                          <div className="bg-amber-50 border border-amber-200 rounded p-3">
                            <Label className="text-amber-800 font-semibold flex items-center gap-2">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                              Ime grupe (Group Name)
                            </Label>
                            <Input
                              value={editingProperty.group_name || ''}
                              onChange={(e) => setEditingProperty({...editingProperty, group_name: e.target.value})}
                              placeholder="npr. Apartments Imperial"
                              className="mt-2"
                            />
                            <p className="text-xs text-amber-700 mt-2">
                              <strong>Važno:</strong> Sve nekretnine s istim "imenom grupe" bit će prikazane zajedno pod tim imenom na stranici za goste.
                              <br />Ostavite prazno ako ne želite grupirati ovu nekretninu s drugima.
                            </p>
                          </div>
                          <div>
                            <Label>Property ID</Label>
                            <Input
                              value={editingProperty.property_id}
                              disabled
                              className="bg-[var(--color-background)]"
                            />
                          </div>
                          <div>
                            <Label>Room IDs (comma-separated)</Label>
                            <Input
                              value={editingProperty.room_ids}
                              onChange={(e) => setEditingProperty({...editingProperty, room_ids: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Prioritet prikaza (Display Order)</Label>
                            <Input
                              type="number"
                              value={editingProperty.display_order || 0}
                              onChange={(e) => setEditingProperty({...editingProperty, display_order: parseInt(e.target.value) || 0})}
                              placeholder="0"
                            />
                            <p className="text-xs text-[var(--color-text-muted)] mt-1">Manji broj = viši prioritet (prikazuje se prvi). 0 = zadani.</p>
                          </div>
                          {/* Pricing Policy Section */}
                          <div className="border-t border-[var(--color-border)] pt-4 mt-4">
                            <Label className="text-sm font-semibold mb-3 block">Pravilnik o cijenama (Pricing Policy)</Label>
                            <p className="text-xs text-[var(--color-text-muted)] mb-3">Tekst koji se prikazuje gostima. Možete dodati pravila o dodatnim gostima, check-in/out vremena, uvjete otkaza itd.</p>
                            <div className="space-y-3">
                              <div>
                                <Label className="text-xs">Hrvatski (HR)</Label>
                                <textarea
                                  className="w-full p-2 border border-[var(--color-border)] rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_hr || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_hr: e.target.value})}
                                  placeholder="• Prijava u objekt moguća je nakon 15:00&#10;• Odjava iz objekta do 10:00&#10;• Dodatni gost: €15 po noći"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">English (EN)</Label>
                                <textarea
                                  className="w-full p-2 border border-[var(--color-border)] rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_en || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_en: e.target.value})}
                                  placeholder="• Check-in after 3:00 PM&#10;• Check-out by 10:00 AM&#10;• Extra guest: €15 per night"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Deutsch (DE)</Label>
                                <textarea
                                  className="w-full p-2 border border-[var(--color-border)] rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_de || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_de: e.target.value})}
                                  placeholder="• Check-in ab 15:00 Uhr&#10;• Check-out bis 10:00 Uhr&#10;• Zusätzlicher Gast: €15 pro Nacht"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Italiano (IT)</Label>
                                <textarea
                                  className="w-full p-2 border border-[var(--color-border)] rounded text-sm min-h-[100px]"
                                  value={editingProperty.pricing_policy_it || ''}
                                  onChange={(e) => setEditingProperty({...editingProperty, pricing_policy_it: e.target.value})}
                                  placeholder="• Check-in dopo le 15:00&#10;• Check-out entro le 10:00&#10;• Ospite extra: €15 a notte"
                                />
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button onClick={handleSaveEdit} size="sm">Save</Button>
                            <Button onClick={() => setEditingProperty(null)} variant="outline" size="sm">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h5 className="font-medium">{prop.property_name}</h5>
                            {prop.group_name && (
                              <p className="text-sm text-amber-700 font-medium flex items-center gap-1 mt-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                Grupa: {prop.group_name}
                              </p>
                            )}
                            <p className="text-sm text-[var(--color-text-secondary)]">Property ID: {prop.property_id}</p>
                            <p className="text-sm text-[var(--color-text-secondary)]">Room IDs: {prop.room_ids.join(', ')}</p>
                            <p className="text-sm text-[var(--color-text-secondary)]">Prioritet: {prop.display_order || 0}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditProperty(prop)}
                              data-testid={`edit-property-${prop.property_id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteProperty(prop.property_id);
                              }}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              data-testid={`delete-property-${prop.property_id}`}
                            >
                              Delete
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Add New Property */}
              <div className="border-t border-[var(--color-border)] pt-6">
                <h4 className="font-medium mb-4">Add New Property</h4>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="propertyId">Property ID</Label>
                    <Input
                      id="propertyId"
                      type="text"
                      value={newProperty.property_id}
                      onChange={(e) => setNewProperty({ ...newProperty, property_id: e.target.value })}
                      placeholder="12345"
                      data-testid="property-id-input"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">Numeric ID from Beds24</p>
                  </div>

                  <div>
                    <Label htmlFor="propertyName">Property Name</Label>
                    <Input
                      id="propertyName"
                      type="text"
                      value={newProperty.property_name}
                      onChange={(e) => setNewProperty({ ...newProperty, property_name: e.target.value })}
                      placeholder="Luxury Downtown Apartment"
                      data-testid="property-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="roomIds">Room IDs (comma-separated)</Label>
                    <Input
                      id="roomIds"
                      type="text"
                      value={newProperty.room_ids}
                      onChange={(e) => setNewProperty({ ...newProperty, room_ids: e.target.value })}
                      placeholder="67890, 67891, 67892"
                      data-testid="room-ids-input"
                    />
                    <p className="text-xs text-[var(--color-text-muted)] mt-1">Enter room IDs separated by commas</p>
                  </div>

                  <Button 
                    onClick={handleAddProperty}
                    data-testid="add-property-button"
                  >
                    Add Property
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card>
            <CardHeader>
              <CardTitle>Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 text-sm text-[var(--color-text-secondary)]">
                <div>
                  <h4 className="font-medium text-[var(--color-text-primary)] mb-2">Step 1: Get Beds24 Invite Code</h4>
                  <ol className="list-decimal list-inside space-y-1 ml-4">
                    <li>Log into your Beds24 account</li>
                    <li>Go to Settings → API (or Marketplace)</li>
                    <li>Select required permissions</li>
                    <li>Click "Generate Invite Code"</li>
                    <li>Copy the invite code and paste it above</li>
                    <li>Click "Test Connection" to verify</li>
                  </ol>
                </div>

                <div>
                  <h4 className="font-medium text-[var(--color-text-primary)] mb-2">Step 2: Find Property & Room IDs</h4>
                  <ol className="list-decimal list-inside space-y-1 ml-4">
                    <li>In Beds24, go to Settings → Properties</li>
                    <li>Click on a property to see its Property ID</li>
                    <li>Go to Settings → Rooms to see all Room IDs</li>
                    <li>Copy the numeric IDs and add them above</li>
                  </ol>
                </div>

                <div className="bg-yellow-50 border-2 border-yellow-400 p-3">
                  <p className="font-medium text-yellow-900 mb-1">Important:</p>
                  <ul className="list-disc list-inside space-y-1 text-yellow-800">
                    <li>Invite codes can only be used once</li>
                    <li>After using an invite code, it's exchanged for an access token automatically</li>
                    <li>Only configured rooms will be available for booking</li>
                    <li>You can edit or delete property configurations anytime</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Property Owners Manager - GDPR Data Controllers */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Property Owners (GDPR Data Controllers)</CardTitle>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                Manage property owners for Privacy Policy and Terms & Conditions compliance
              </p>
            </CardHeader>
            <CardContent>
              <PropertyOwnersManager />
            </CardContent>
          </Card>
          </>
          )}

          {/* Room Media Tab Content */}
          {activeTab === 'media' && (
            <RoomMediaManager />
          )}

          {/* Theme & Branding Tab Content */}
          {activeTab === 'theme' && (
            <ThemeSettings />
          )}

          {/* Email Settings Tab Content */}
          {activeTab === 'email' && (
            <EmailSettingsManager />
          )}

          {activeTab === 'promo' && (
            <PromoCodesManager />
          )}

          {activeTab === 'cards' && (
            <InfoCardsManager />
          )}

          {activeTab === 'rates' && (
            <RatesManager token={localStorage.getItem('admin_token')} />
          )}

          {activeTab === 'campmap' && (
            <CampMapEditor />
          )}
          {activeTab === 'custommap' && (
            <CustomMapManager
              token={localStorage.getItem('admin_token')}
              propertyConfigs={settings.property_configs || []}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;